import * as React from "react";

import { Badge } from "@/components/ui/badge";
import { METRIC_TOOLTIP_BODY_CLASS, METRIC_TOOLTIP_TITLE_CLASS } from "@/components/ui/metric-tooltip";
import {
  Tooltip,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import type { ConditionAnalysisSlice } from "../../../../utils/conditionAnalysisSelectors";
import { regimeDrilldownTableDescription } from "../../../../utils/marketContextDrilldownLabels";
import {
  getSliceCategoryBadgeGradient,
  getSliceCategoryLabel,
  getSliceCategoryShortLabel,
  sliceKindToDrilldownTableKey,
} from "../marketContextSliceDisplay";
import { MarketContextTooltipContent } from "./marketContextDataTableShared";

/** Matches Report Overview / Trade Log gradient meta badges (shadcn Badge + bg image). */
const SLICE_CATEGORY_BADGE_CLASS =
  "max-w-full truncate border-0 bg-transparent font-[family-name:var(--font-primary)] text-xs font-medium normal-case tracking-normal text-white shadow-none";

function CategoryBadgeChrome({
  slice,
  label,
  title,
}: {
  slice: ConditionAnalysisSlice;
  label: string;
  title: string;
}): React.ReactElement {
  return (
    <Badge
      data-market-context-category-badge=""
      title={title}
      className={SLICE_CATEGORY_BADGE_CLASS}
      style={
        {
          "--market-context-category-gradient": getSliceCategoryBadgeGradient(slice),
        } as React.CSSProperties
      }
    >
      {label}
    </Badge>
  );
}

export function MarketContextSliceCategoryBadge({
  slice,
  label,
  withTooltip = true,
  candleTimeframe,
}: {
  slice: ConditionAnalysisSlice;
  /**
   * When set, shown on the badge instead of the compact category label.
   * Tooltip still uses the full category name unless this equals the full label.
   */
  label?: string;
  /** When false, render badge only (caller provides its own tooltip). */
  withTooltip?: boolean;
  /** Report candle TF — scales long/short direction bar counts in labels. */
  candleTimeframe?: string | null;
}): React.ReactElement {
  const fullLabel = getSliceCategoryLabel(slice.kind, candleTimeframe);
  const badgeLabel = label ?? getSliceCategoryShortLabel(slice.kind, candleTimeframe);

  if (!withTooltip) {
    return <CategoryBadgeChrome slice={slice} label={badgeLabel} title={fullLabel} />;
  }

  const tableKey = sliceKindToDrilldownTableKey(slice.kind);
  const description = tableKey
    ? regimeDrilldownTableDescription(tableKey, candleTimeframe)
    : `Groups trades by ${fullLabel} at entry.`;

  return (
    <TooltipProvider delayDuration={300}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="inline-flex max-w-full min-w-0 cursor-help" data-no-row-click="">
            <CategoryBadgeChrome slice={slice} label={badgeLabel} title={fullLabel} />
          </span>
        </TooltipTrigger>
        <MarketContextTooltipContent widthClass="max-w-[300px]">
          <p className={METRIC_TOOLTIP_TITLE_CLASS}>{fullLabel}</p>
          <p className={`mt-1 text-[11px] leading-snug ${METRIC_TOOLTIP_BODY_CLASS}`}>{description}</p>
        </MarketContextTooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
